import {area as turfArea} from '@turf/turf';
import React, {useMemo} from 'react';

import {SetupSectionProps, getStateViewDefaults, setupWizardApi} from 'src/lib/api/setupWizard';
import {buttonSecondary} from 'src/lib/tailwindClasses';

import {ApiErrorMessage} from './ApiErrorMessage';
import {CliCommandBlock} from './CliCommandBlock';
import DelineationMap from './DelineationMap';
import ReadonlyDataTable from './ReadonlyDataTable';
import {PrerequisiteWarning} from './SetupStepSection';
import {downloadBlob, downloadCsv} from './shared';
import {useSetupMutation} from './useSetupMutation';

export default function ReviewApplySection({
  orgId,
  projectId,
  payload,
  queryKey,
  prerequisiteWarning,
}: SetupSectionProps) {
  const view = useMemo(() => getStateViewDefaults(payload?.data), [payload?.data]);
  const {
    mutate,
    mutatingAction,
    isMutating,
    error: mutationError,
    lastCliCommand,
  } = useSetupMutation(queryKey);
  const [selectedForecastPoint, setSelectedForecastPoint] = React.useState<string | null>(null);
  const [isDownloadingGpkg, setIsDownloadingGpkg] = React.useState(false);

  const selectableSites = React.useMemo(() => {
    const uniqueSites = new Set<string>();
    for (const site of view.forecastPoints) {
      if (typeof site === 'string' && site.trim()) {
        uniqueSites.add(site.trim());
      }
    }
    return Array.from(uniqueSites).sort((a, b) => a.localeCompare(b));
  }, [view.forecastPoints]);

  const contributingSites = React.useMemo(() => {
    if (!selectedForecastPoint) return [];
    return view.contributingBasinsByForecastPoint?.[selectedForecastPoint] ?? [];
  }, [selectedForecastPoint, view.contributingBasinsByForecastPoint]);

  // Compute drainage area (km²) for the selected forecast point from its contributing subbasin geometries
  const selectedDrainageAreaKm2 = React.useMemo(() => {
    const basins = view.delineationGeojson?.basins;
    if (!basins || contributingSites.length === 0) return null;
    const subSet = new Set(contributingSites);
    let total = 0;
    for (const f of basins.features) {
      const name = (f.properties as Record<string, unknown>)?.site_name;
      if (typeof name === 'string' && subSet.has(name) && f.geometry) {
        total += turfArea(f as GeoJSON.Feature) / 1e6;
      }
    }
    return total > 0 ? Math.round(total * 100) / 100 : null;
  }, [view.delineationGeojson?.basins, contributingSites]);

  // Build basin grouping: group subbasins by their outlet (top-level basin).
  // Each forecast point has an outlet_name in outputDelineation; all subbasins
  // contributing to forecast points sharing the same outlet get the same color.
  const {basinGroupNames, subbasinToBasinGroup, drainageAreaByBasinGroup} = React.useMemo(() => {
    const contributing = view.contributingBasinsByForecastPoint;
    const basins = view.delineationGeojson?.basins;
    if (!contributing)
      return {
        basinGroupNames: [] as string[],
        subbasinToBasinGroup: {},
        drainageAreaByBasinGroup: {},
      };

    // Map each forecast point to its outlet_name
    const fpToOutlet: Record<string, string> = {};
    for (const row of view.outputDelineation) {
      const name = row.name;
      const outlet = row.outlet_name;
      if (name && outlet) fpToOutlet[name] = outlet;
    }

    // Build subbasin → outlet mapping
    const subMap: Record<string, string> = {};
    const outletSet = new Set<string>();
    for (const [fp, subs] of Object.entries(contributing)) {
      const outlet = fpToOutlet[fp] ?? fp;
      outletSet.add(outlet);
      for (const sub of subs) {
        subMap[sub] = outlet;
      }
    }

    // Compute drainage area per basin group from subbasin geometries
    const areaByGroup: Record<string, number> = {};
    if (basins) {
      for (const f of basins.features) {
        const name = (f.properties as Record<string, unknown>)?.site_name;
        if (typeof name === 'string' && f.geometry && subMap[name]) {
          const group = subMap[name];
          // turfArea returns m², convert to km²
          areaByGroup[group] = (areaByGroup[group] ?? 0) + turfArea(f as GeoJSON.Feature) / 1e6;
        }
      }
      // Round after summing
      for (const key of Object.keys(areaByGroup)) {
        areaByGroup[key] = Math.round(areaByGroup[key] * 100) / 100;
      }
    }

    return {
      basinGroupNames: Array.from(outletSet).sort((a, b) => a.localeCompare(b)),
      subbasinToBasinGroup: subMap,
      drainageAreaByBasinGroup: areaByGroup,
    };
  }, [
    view.contributingBasinsByForecastPoint,
    view.outputDelineation,
    view.delineationGeojson?.basins,
  ]);

  React.useEffect(() => {
    if (selectedForecastPoint && !selectableSites.includes(selectedForecastPoint)) {
      setSelectedForecastPoint(null);
    }
  }, [selectableSites, selectedForecastPoint]);

  const handleApplyDelineationToSiteInfo = async () => {
    await mutate('apply-delineation', () => setupWizardApi.applyDelineation(orgId, projectId));
  };

  const handleDownloadDelineationCsv = () => {
    downloadCsv(view.outputDelineation, `${projectId}_delineation.csv`);
  };

  const [isDownloadingCsv, setIsDownloadingCsv] = React.useState(false);
  const handleDownloadSetupCsv = async () => {
    setIsDownloadingCsv(true);
    try {
      const blob = await setupWizardApi.downloadSetupCsv(orgId, projectId);
      downloadBlob(blob, `${projectId}_setup.csv`);
    } catch (e) {
      alert(e instanceof Error ? e.message : 'Download failed');
    } finally {
      setIsDownloadingCsv(false);
    }
  };

  const handleDownloadGeopackage = async () => {
    setIsDownloadingGpkg(true);
    try {
      const blob = await setupWizardApi.downloadGeopackage(orgId, projectId);
      downloadBlob(blob, `${projectId}_basins.gpkg`);
    } catch (e) {
      alert(e instanceof Error ? e.message : 'Download failed');
    } finally {
      setIsDownloadingGpkg(false);
    }
  };

  if (!view.hasDelineation) {
    return (
      <div>
        {prerequisiteWarning && <PrerequisiteWarning message={prerequisiteWarning} />}
        {!prerequisiteWarning && (
          <p className="text-sm text-gray-500">
            Run delineation in the previous step to see results here.
          </p>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <ApiErrorMessage message={mutationError} />
      <CliCommandBlock command={lastCliCommand} />

      {view.delineationGeojson && (
        <div>
          <div className="flex items-center gap-3 mb-2">
            {selectableSites.length > 0 && (
              <div>
                <label className="text-xs font-medium text-gray-600 mr-2">
                  Highlight forecast point:
                </label>
                <select
                  className="text-sm border-gray-300 rounded px-1 py-0.5"
                  value={selectedForecastPoint ?? ''}
                  onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                    setSelectedForecastPoint(e.target.value || null)
                  }
                >
                  <option value="">None</option>
                  {selectableSites.map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </select>
                {selectedForecastPoint && contributingSites.length > 0 && (
                  <p className="mt-1 text-xs text-gray-600">
                    Contributing subbasins: {contributingSites.length}
                    {selectedDrainageAreaKm2 != null && (
                      <>
                        {' '}
                        &middot; Drainage area: {selectedDrainageAreaKm2.toLocaleString()} km&sup2;
                      </>
                    )}
                  </p>
                )}
                {selectedForecastPoint && contributingSites.length === 0 && (
                  <p className="mt-1 text-xs text-amber-700">
                    Highlight unavailable for this forecast point.
                  </p>
                )}
              </div>
            )}
          </div>
          <DelineationMap
            basins={view.delineationGeojson.basins}
            rivers={view.delineationGeojson.rivers}
            siteInfo={view.siteInfo}
            publicGauges={view.publicGauges}
            contributingSites={contributingSites}
            basinGroupNames={basinGroupNames}
            subbasinToBasinGroup={subbasinToBasinGroup}
            drainageAreaByBasinGroup={drainageAreaByBasinGroup}
          />
        </div>
      )}

      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          disabled={view.outputDelineation.length === 0}
          className={buttonSecondary}
          onClick={handleDownloadDelineationCsv}
        >
          Download Delineation CSV
        </button>
        <button
          type="button"
          disabled={isDownloadingCsv || view.siteInfo.length === 0}
          className={buttonSecondary}
          onClick={handleDownloadSetupCsv}
        >
          {isDownloadingCsv ? 'Downloading\u2026' : 'Download Setup CSV'}
        </button>
        <button
          type="button"
          disabled={isDownloadingGpkg}
          className={buttonSecondary}
          onClick={handleDownloadGeopackage}
        >
          {isDownloadingGpkg ? 'Downloading…' : 'Download Geopackage'}
        </button>
      </div>

      <div className="flex items-center gap-3">
        <button
          type="button"
          disabled={isMutating || view.outputDelineation.length === 0}
          className="rounded-md bg-green-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-green-500 disabled:opacity-50"
          onClick={handleApplyDelineationToSiteInfo}
          title="Populate site info from delineation output (overwrites existing site info)"
        >
          {mutatingAction === 'apply-delineation' ? 'Applying…' : 'Apply Delineation to Site Info'}
        </button>
        <span className="text-xs text-gray-500">
          Overwrites the site table with delineation-derived data (gauges, basins, etc.)
        </span>
      </div>

      <ReadonlyDataTable rows={view.outputDelineation} title="Output Delineation" />

      <ReadonlyDataTable
        rows={view.publicGauges}
        title="Public Gauges"
        description="Sites that may use public gauge data (non-private source of observations)."
      />
    </div>
  );
}
